// ─── Panel · Invitaciones al pliego ─────────────────────────────────────────
// Sección del Panel HAUS Admin para crear, previsualizar y enviar
// invitaciones al pliego BREEAM del proyecto activo.
//
// Flujo:
//  1. Admin rellena formulario (email destinatario + rol + ramas visibles +
//     etiqueta interna + fecha de expiración opcional).
//  2. Pulsa "Crear y previsualizar correo".
//     · Genera token único.
//     · INSERT en pliego_invitaciones (con email_destinatario).
//     · Abre modal con destinatario/asunto/cuerpo EDITABLES prerrellenados.
//  3. En el modal:
//     · Botón "Enviar" → invoca Edge Function `enviar-correo`.
//     · Botón "Cancelar" → borra la invitación recién creada (rollback).
//
// Listado inferior: invitaciones activas con estado y botón "Reenviar".

import { useCallback, useEffect, useState } from 'react'
import { supabase } from '../lib/supabase'
import Modal from './Modal'

const C = {
  text: '#2C2017', muted: '#7A6A5A', border: '#DDD5C8',
  bg: '#FFFFFF', bgAlt: '#FAF7F3', accent: '#0E2C4B',
  olive: '#8D9A2E', amber: '#C98A2E', coral: '#D4614A',
}
const FONT = "'DM Sans', sans-serif"

const CARD = {
  background: C.bg, borderRadius: 8, padding: '1.25rem',
  boxShadow: '0 1px 4px rgba(59,47,47,0.07)',
  border: `1px solid ${C.border}`, fontFamily: FONT,
}

const ROLES = [
  { value: 'arquitectura', label: 'Arquitectura', ramasSugeridas: ['Diseño', 'Instalaciones', 'Contratista'] },
  { value: 'ingenieria',   label: 'Ingeniería',   ramasSugeridas: ['Instalaciones'] },
  { value: 'contratista',  label: 'Contratista',  ramasSugeridas: ['Contratista'] },
  { value: 'promotor',     label: 'Promotor',     ramasSugeridas: ['Diseño', 'Instalaciones', 'Contratista'] },
]
const ROL_LABEL = { arquitectura: 'Arquitectura', ingenieria: 'Ingeniería', contratista: 'Contratista', promotor: 'Promotor' }
const RAMAS_SELECCIONABLES = ['Diseño', 'Instalaciones', 'Contratista']  // HAUS nunca visible a externos

const label = {
  display: 'block', fontSize: '0.7rem', color: C.muted, fontWeight: 700,
  textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '0.3rem',
  fontFamily: FONT,
}
const input = {
  width: '100%', padding: '0.55rem 0.7rem', border: `1px solid ${C.border}`,
  borderRadius: 4, fontSize: '0.88rem', fontFamily: FONT, boxSizing: 'border-box',
  color: C.text, background: C.bg,
}
const btnPrimary = {
  padding: '0.6rem 1.1rem', background: C.accent, color: '#fff', border: 'none',
  borderRadius: 4, fontSize: '0.85rem', fontWeight: 600, cursor: 'pointer',
  fontFamily: FONT,
}
const btnSecondary = {
  padding: '0.6rem 1.1rem', background: '#fff', color: C.text,
  border: `1px solid ${C.border}`, borderRadius: 4, fontSize: '0.85rem',
  fontWeight: 500, cursor: 'pointer', fontFamily: FONT,
}
const btnDanger = { ...btnSecondary, color: C.coral, borderColor: C.coral }

// ── Utilidades ─────────────────────────────────────────────────────────────

function generarToken() {
  // 32 hex chars sin guiones · suficientemente entropía y URL-safe.
  const uuid = (crypto.randomUUID?.() || `${Date.now()}-${Math.random()}`)
  return uuid.replace(/-/g, '')
}

function ramaLabel(r) { return r === 'Instalaciones' ? 'Ingeniería' : r }

function urlInvitacion(token) {
  const origin = typeof window !== 'undefined' ? window.location.origin : ''
  return `${origin}/invitacion/${token}`
}

function plantillaCorreo({ nombreProyecto, rol, ramas }) {
  const rolLabel = ROL_LABEL[rol] || rol
  const ramasStr = (ramas || []).map(ramaLabel).join(', ')
  const asunto = `Invitación al pliego BREEAM · ${nombreProyecto || 'Proyecto'}`
  const cuerpo =
`Hola,

Te enviamos el acceso al pliego BREEAM del proyecto ${nombreProyecto || ''} como equipo de ${rolLabel}.

Podrás consultar las fichas de ${ramasStr} y subir la documentación de tus responsabilidades directamente en la plataforma.

En el primer acceso te pediremos nombre, email y contraseña. El enlace de acceso es personal y no debe compartirse.

Cualquier duda, respondemos aquí mismo.

Un saludo,
Equipo HAUS`
  return { asunto, cuerpo }
}

// ── Componente ─────────────────────────────────────────────────────────────

export default function InvitacionesPanel({ projectId, projectName }) {
  // Formulario
  const [email, setEmail] = useState('')
  const [nombreContacto, setNombreContacto] = useState('')
  const [etiqueta, setEtiqueta] = useState('')
  const [rol, setRol] = useState('arquitectura')
  const [ramas, setRamas] = useState(['Diseño', 'Instalaciones', 'Contratista'])
  const [expiraEn, setExpiraEn] = useState('')  // yyyy-mm-dd o vacío

  // Modal
  const [modalOpen, setModalOpen] = useState(false)
  const [modalTo, setModalTo] = useState('')
  const [modalSubject, setModalSubject] = useState('')
  const [modalBody, setModalBody] = useState('')
  const [modalInvId, setModalInvId] = useState(null)   // para rollback si cancela
  const [modalToken, setModalToken] = useState(null)
  const [modalMode, setModalMode] = useState('nueva')  // 'nueva' | 'reenvio'
  const [enviando, setEnviando] = useState(false)

  // Listado
  const [invitaciones, setInvitaciones] = useState([])
  const [cargandoLista, setCargandoLista] = useState(false)
  const [feedback, setFeedback] = useState(null)  // { tipo: 'ok'|'error', texto }

  const cargarLista = useCallback(async () => {
    if (!projectId) return
    setCargandoLista(true)
    const { data, error } = await supabase
      .from('pliego_invitaciones')
      .select('id, token, pliego_rol, pliego_visible_ramas, etiqueta, nombre_contacto, email_destinatario, creada_at, expira_at, activo, primera_aceptacion_at')
      .eq('project_id', projectId)
      .order('creada_at', { ascending: false })
    if (!error) setInvitaciones(data || [])
    setCargandoLista(false)
  }, [projectId])

  useEffect(() => { cargarLista() }, [cargarLista])

  function seleccionarRol(nuevoRol) {
    setRol(nuevoRol)
    const rolCfg = ROLES.find(r => r.value === nuevoRol)
    if (rolCfg) setRamas(rolCfg.ramasSugeridas)
  }

  function toggleRama(r) {
    setRamas(prev => prev.includes(r) ? prev.filter(x => x !== r) : [...prev, r])
  }

  async function handleCrearYPrevisualizar() {
    setFeedback(null)
    if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setFeedback({ tipo: 'error', texto: 'Introduce un email válido.' })
      return
    }
    if (!nombreContacto.trim() || nombreContacto.trim().split(/\s+/).length < 2) {
      setFeedback({ tipo: 'error', texto: 'Nombre y apellido obligatorios (al menos 2 palabras).' })
      return
    }
    if (ramas.length === 0) {
      setFeedback({ tipo: 'error', texto: 'Selecciona al menos una rama visible.' })
      return
    }
    if (!projectId) {
      setFeedback({ tipo: 'error', texto: 'No hay proyecto activo.' })
      return
    }

    const token = generarToken()
    const { data: userData } = await supabase.auth.getUser()
    const insertPayload = {
      token,
      project_id: projectId,
      pliego_rol: rol,
      pliego_visible_ramas: ramas,
      etiqueta: etiqueta || null,
      nombre_contacto: nombreContacto.trim(),
      email_destinatario: email.trim().toLowerCase(),
      expira_at: expiraEn ? new Date(expiraEn).toISOString() : null,
      creada_por: userData?.user?.id || null,
      activo: true,
    }

    const { data: inv, error } = await supabase
      .from('pliego_invitaciones')
      .insert(insertPayload)
      .select('id, token')
      .single()

    if (error || !inv) {
      setFeedback({ tipo: 'error', texto: `Error creando invitación: ${error?.message || 'desconocido'}` })
      return
    }

    const { asunto, cuerpo } = plantillaCorreo({
      nombreProyecto: projectName,
      rol,
      ramas,
    })

    setModalTo(email.trim().toLowerCase())
    setModalSubject(asunto)
    setModalBody(cuerpo)
    setModalInvId(inv.id)
    setModalToken(inv.token)
    setModalMode('nueva')
    setModalOpen(true)
  }

  async function handleReenviar(inv) {
    if (!inv?.email_destinatario) {
      setFeedback({ tipo: 'error', texto: 'Esta invitación no tiene email destinatario guardado. Crea una nueva o edita el campo antes de enviar.' })
    }
    const { asunto, cuerpo } = plantillaCorreo({
      nombreProyecto: projectName,
      rol: inv.pliego_rol,
      ramas: inv.pliego_visible_ramas,
    })
    setModalTo(inv.email_destinatario || '')
    setModalSubject(asunto)
    setModalBody(cuerpo)
    setModalInvId(inv.id)
    setModalToken(inv.token)
    setModalMode('reenvio')
    setFeedback(null)
    setModalOpen(true)
  }

  async function handleEnviarCorreo() {
    if (!modalTo || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(modalTo)) {
      setFeedback({ tipo: 'error', texto: 'Email destinatario inválido.' })
      return
    }
    if (!modalSubject.trim() || !modalBody.trim()) {
      setFeedback({ tipo: 'error', texto: 'Asunto y cuerpo son obligatorios.' })
      return
    }
    setEnviando(true)
    setFeedback(null)
    try {
      const { data, error } = await supabase.functions.invoke('enviar-correo', {
        body: {
          to: modalTo.trim().toLowerCase(),
          subject: modalSubject.trim(),
          body_text: modalBody,
          cta_url: urlInvitacion(modalToken || ''),
          cta_label: 'Acceder al pliego',
          contexto: 'invitacion_pliego',
          referencia_id: modalInvId,
        },
      })
      if (error) throw new Error(error.message || 'Fallo al invocar la función')
      if (!data?.ok) throw new Error(data?.error || 'Respuesta inesperada')

      setFeedback({ tipo: 'ok', texto: `Correo enviado a ${modalTo}.` })
      setModalOpen(false)
      resetFormulario()
      cargarLista()
    } catch (err) {
      setFeedback({ tipo: 'error', texto: `Error enviando: ${err?.message || err}` })
    } finally {
      setEnviando(false)
    }
  }

  async function handleCancelarModal() {
    // Si es una invitación recién creada (modo 'nueva') y no se envió,
    // borramos la fila para no dejar invitaciones huérfanas.
    if (modalMode === 'nueva' && modalInvId) {
      await supabase.from('pliego_invitaciones').delete().eq('id', modalInvId)
    }
    setModalOpen(false)
    if (modalMode === 'nueva') cargarLista()
  }

  async function handleEliminar(inv) {
    const nombre = inv.email_destinatario || inv.etiqueta || inv.token
    const avisoAceptada = inv.primera_aceptacion_at
      ? '\n\n⚠️ Esta invitación ya fue aceptada por alguien. Eliminarla NO borra las cuentas creadas · esos usuarios pueden seguir logueándose.'
      : ''
    if (!window.confirm(`¿Eliminar la invitación de ${nombre}?${avisoAceptada}\n\nEsta acción no se puede deshacer.`)) return
    const { error } = await supabase
      .from('pliego_invitaciones')
      .delete()
      .eq('id', inv.id)
    if (error) {
      setFeedback({ tipo: 'error', texto: `No se pudo eliminar: ${error.message}` })
    } else {
      setFeedback({ tipo: 'ok', texto: `Invitación de ${nombre} eliminada.` })
      cargarLista()
    }
  }

  async function handleDesactivar(inv) {
    if (!window.confirm(`¿Desactivar la invitación de ${inv.email_destinatario || inv.etiqueta || inv.token}? El enlace dejará de funcionar.`)) return
    const { error } = await supabase
      .from('pliego_invitaciones')
      .update({ activo: false })
      .eq('id', inv.id)
    if (error) {
      setFeedback({ tipo: 'error', texto: `No se pudo desactivar: ${error.message}` })
    } else {
      cargarLista()
    }
  }

  function resetFormulario() {
    setEmail(''); setNombreContacto(''); setEtiqueta(''); setRol('arquitectura')
    setRamas(['Diseño', 'Instalaciones', 'Contratista']); setExpiraEn('')
    setModalInvId(null); setModalToken(null)
  }

  return (
    <div style={{ marginTop: '1.5rem' }}>
      <h2 style={{
        fontSize: '0.95rem', fontWeight: 600, color: C.text, margin: '0 0 0.75rem',
        fontFamily: FONT,
      }}>
        Invitaciones al pliego
      </h2>

      <div style={{ ...CARD, marginBottom: '1rem' }}>
        <div style={{ fontSize: '0.8rem', color: C.muted, marginBottom: '1rem' }}>
          Genera un enlace de acceso al pliego y envíalo por correo desde <strong>notificaciones@appbyhaus.com</strong>.
          Antes de enviarse verás una vista previa editable del correo.
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1rem' }}>
          <div>
            <label style={label}>Email destinatario *</label>
            <input style={input} type="email" value={email}
                   onChange={(e) => setEmail(e.target.value)}
                   placeholder="nombre@empresa.com" />
          </div>
          <div>
            <label style={label}>Nombre y apellido *</label>
            <input style={input} type="text" value={nombreContacto}
                   onChange={(e) => setNombreContacto(e.target.value)}
                   placeholder="ej. Martina Mottironi" />
          </div>
        </div>

        <div style={{ marginBottom: '1rem' }}>
          <label style={label}>Etiqueta/empresa (opcional)</label>
          <input style={input} type="text" value={etiqueta}
                 onChange={(e) => setEtiqueta(e.target.value)}
                 placeholder="ej. Coderch Urbanismo" />
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '200px 1fr 180px', gap: '1rem', marginBottom: '1rem' }}>
          <div>
            <label style={label}>Rol *</label>
            <select style={input} value={rol} onChange={(e) => seleccionarRol(e.target.value)}>
              {ROLES.map(r => (
                <option key={r.value} value={r.value}>{r.label}</option>
              ))}
            </select>
          </div>
          <div>
            <label style={label}>Ramas visibles *</label>
            <div style={{ display: 'flex', gap: '0.75rem', paddingTop: '0.35rem' }}>
              {RAMAS_SELECCIONABLES.map(r => (
                <label key={r} style={{
                  display: 'inline-flex', alignItems: 'center', gap: '0.35rem',
                  fontSize: '0.82rem', color: C.text, cursor: 'pointer',
                }}>
                  <input type="checkbox" checked={ramas.includes(r)}
                         onChange={() => toggleRama(r)} />
                  {ramaLabel(r)}
                </label>
              ))}
            </div>
          </div>
          <div>
            <label style={label}>Expira (opcional)</label>
            <input style={input} type="date" value={expiraEn}
                   onChange={(e) => setExpiraEn(e.target.value)} />
          </div>
        </div>

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.5rem' }}>
          <button style={btnPrimary} onClick={handleCrearYPrevisualizar}>
            Crear y previsualizar correo
          </button>
        </div>

        {feedback && (
          <div style={{
            marginTop: '0.85rem', padding: '0.55rem 0.8rem', borderRadius: 4,
            fontSize: '0.82rem',
            background: feedback.tipo === 'ok' ? '#EFF5E1' : '#FFF1ED',
            color:      feedback.tipo === 'ok' ? '#4A5A0F' : C.coral,
            border: `1px solid ${feedback.tipo === 'ok' ? C.olive : C.coral}`,
          }}>
            {feedback.texto}
          </div>
        )}
      </div>

      <div style={CARD}>
        <div style={{
          fontSize: '0.82rem', fontWeight: 600, color: C.text,
          marginBottom: '0.75rem', display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        }}>
          <span>Invitaciones ({invitaciones.length})</span>
          <button style={btnSecondary} onClick={cargarLista} disabled={cargandoLista}>
            {cargandoLista ? 'Cargando…' : 'Recargar'}
          </button>
        </div>

        {invitaciones.length === 0 && !cargandoLista && (
          <div style={{ fontSize: '0.82rem', color: C.muted, padding: '0.5rem 0' }}>
            Aún no hay invitaciones para este proyecto.
          </div>
        )}

        {invitaciones.length > 0 && (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.82rem' }}>
              <thead>
                <tr style={{ textAlign: 'left', color: C.muted, borderBottom: `1px solid ${C.border}` }}>
                  <th style={{ padding: '0.45rem 0.5rem' }}>Destinatario</th>
                  <th style={{ padding: '0.45rem 0.5rem' }}>Nombre contacto</th>
                  <th style={{ padding: '0.45rem 0.5rem' }}>Etiqueta</th>
                  <th style={{ padding: '0.45rem 0.5rem' }}>Rol</th>
                  <th style={{ padding: '0.45rem 0.5rem' }}>Ramas</th>
                  <th style={{ padding: '0.45rem 0.5rem' }}>Creada</th>
                  <th style={{ padding: '0.45rem 0.5rem' }}>Estado</th>
                  <th style={{ padding: '0.45rem 0.5rem' }}></th>
                </tr>
              </thead>
              <tbody>
                {invitaciones.map(inv => {
                  const expirada = inv.expira_at && new Date(inv.expira_at) < new Date()
                  const aceptada = !!inv.primera_aceptacion_at
                  let estadoTxt, estadoColor
                  if (!inv.activo)       { estadoTxt = 'Desactivada'; estadoColor = C.muted }
                  else if (expirada)     { estadoTxt = 'Expirada';    estadoColor = C.amber }
                  else if (aceptada)     { estadoTxt = 'Activa';      estadoColor = C.olive }
                  else                   { estadoTxt = 'Enviada';     estadoColor = C.accent }
                  return (
                    <tr key={inv.id} style={{ borderBottom: `1px solid ${C.border}` }}>
                      <td style={{ padding: '0.45rem 0.5rem', color: C.text }}>
                        {inv.email_destinatario || <span style={{ color: C.muted }}>—</span>}
                      </td>
                      <td style={{ padding: '0.45rem 0.5rem', color: C.text }}>
                        {inv.nombre_contacto || <span style={{ color: C.coral, fontStyle: 'italic' }}>falta</span>}
                        {' '}
                        <button
                          onClick={async () => {
                            const nuevo = window.prompt('Nombre y apellido del contacto:', inv.nombre_contacto || '')
                            if (nuevo === null) return  // cancelado
                            const limpio = nuevo.trim()
                            if (!limpio || limpio.split(/\s+/).length < 2) {
                              alert('Introduce nombre y apellido (al menos 2 palabras).')
                              return
                            }
                            const { error } = await supabase
                              .from('pliego_invitaciones')
                              .update({ nombre_contacto: limpio })
                              .eq('id', inv.id)
                            if (error) alert('Error: ' + error.message)
                            else cargarLista()
                          }}
                          style={{ border: 'none', background: 'transparent', cursor: 'pointer', color: C.accent, fontSize: '0.72rem', padding: 0, textDecoration: 'underline' }}
                          title="Editar nombre"
                        >
                          editar
                        </button>
                      </td>
                      <td style={{ padding: '0.45rem 0.5rem', color: C.text }}>
                        {inv.etiqueta || <span style={{ color: C.muted }}>—</span>}
                      </td>
                      <td style={{ padding: '0.45rem 0.5rem', color: C.text }}>
                        {ROL_LABEL[inv.pliego_rol] || inv.pliego_rol}
                      </td>
                      <td style={{ padding: '0.45rem 0.5rem', color: C.muted, fontSize: '0.78rem' }}>
                        {(inv.pliego_visible_ramas || []).map(ramaLabel).join(' · ')}
                      </td>
                      <td style={{ padding: '0.45rem 0.5rem', color: C.muted, fontSize: '0.78rem' }}>
                        {inv.creada_at ? new Date(inv.creada_at).toLocaleDateString('es-ES') : ''}
                      </td>
                      <td style={{ padding: '0.45rem 0.5rem' }}>
                        <span style={{
                          display: 'inline-block', padding: '0.15rem 0.5rem', borderRadius: 3,
                          fontSize: '0.72rem', background: `${estadoColor}18`, color: estadoColor,
                          fontWeight: 600,
                        }}>{estadoTxt}</span>
                      </td>
                      <td style={{ padding: '0.45rem 0.5rem', textAlign: 'right', whiteSpace: 'nowrap' }}>
                        {inv.activo && !expirada && (
                          <>
                            <button style={{ ...btnSecondary, padding: '0.3rem 0.6rem', fontSize: '0.75rem', marginRight: 4 }}
                                    onClick={() => handleReenviar(inv)}>
                              Reenviar correo
                            </button>
                            <button style={{ ...btnDanger, padding: '0.3rem 0.6rem', fontSize: '0.75rem', marginRight: 4 }}
                                    onClick={() => handleDesactivar(inv)}>
                              Desactivar
                            </button>
                          </>
                        )}
                        <button style={{ ...btnDanger, padding: '0.3rem 0.6rem', fontSize: '0.75rem' }}
                                onClick={() => handleEliminar(inv)}
                                title="Elimina la invitación de la lista (no borra cuentas ya creadas)">
                          Eliminar
                        </button>
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <Modal
        isOpen={modalOpen}
        onClose={handleCancelarModal}
        title={modalMode === 'reenvio' ? 'Reenviar correo de invitación' : 'Previsualizar y enviar correo'}
        maxWidth={640}
        actions={
          <>
            <button style={btnSecondary} onClick={handleCancelarModal} disabled={enviando}>
              Cancelar
            </button>
            <button style={{ ...btnPrimary, opacity: enviando ? 0.6 : 1 }}
                    onClick={handleEnviarCorreo} disabled={enviando}>
              {enviando ? 'Enviando…' : 'Enviar correo'}
            </button>
          </>
        }
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.85rem' }}>
          <div>
            <label style={label}>De</label>
            <div style={{ ...input, background: C.bgAlt, color: C.muted }}>
              HAUS &lt;notificaciones@appbyhaus.com&gt;
            </div>
          </div>
          <div>
            <label style={label}>Para *</label>
            <input style={input} type="email" value={modalTo}
                   onChange={(e) => setModalTo(e.target.value)} />
          </div>
          <div>
            <label style={label}>Asunto *</label>
            <input style={input} type="text" value={modalSubject}
                   onChange={(e) => setModalSubject(e.target.value)} />
          </div>
          <div>
            <label style={label}>Cuerpo del correo *</label>
            <textarea style={{ ...input, minHeight: 260, fontFamily: 'inherit', resize: 'vertical' }}
                      value={modalBody}
                      onChange={(e) => setModalBody(e.target.value)} />
            <div style={{ fontSize: '0.72rem', color: C.muted, marginTop: '0.3rem' }}>
              El enlace se añade automáticamente al final del correo como botón "Acceder al pliego":<br />
              <code style={{ color: C.accent }}>{urlInvitacion(modalToken || '')}</code>
            </div>
          </div>
        </div>
      </Modal>
    </div>
  )
}
